import {
  Component,
  EventEmitter,
  h,
  Host,
  Prop,
  Event,
  Element,
  State,
  ComponentInterface,
  Watch,
  Listen
} from '@stencil/core';
import { JuiControlSize, JuiValidationEvents } from '../../../shared/form-controls/jui-form-control.model';
import { RadioDisplay } from './jui-radio-group.model';
import {
  renderFormError,
  renderTooltipOnLeft,
  renderTooltipOnBottom
} from '../../../shared/form-controls/jui-form-control.utils';
import { IconSize, IconVariant } from '../../jui-icon/jui-icon.model';

@Component({
  tag: 'jui-radio-group',
  styleUrl: 'jui-radio-group.scss',
  shadow: true,
  formAssociated: true
})
export class JuiRadioGroup implements ComponentInterface {
  /**
   * Label to display for the radio input.
   */
  @Prop() label = '';

  /**
   * The form name for the radio group control.
   */
  @Prop() name = '';

  /**
   * Display can be stacked (default) or inline.
   */
  @Prop() display: RadioDisplay = RadioDisplay.stacked;

  /**
   * Value to hold the current value for the radio group.
   */
  @Prop({ attribute: 'value', reflect: true, mutable: true }) value = '';

  /**
   * Marking the radio group as required. Defaults to false
   */
  @Prop() required = false;

  /**
   * Marking the input field as disabled. Defaults to false
   */
  @Prop() disable: boolean;

  /**
   * The event to trigger validation check
   */
  @Prop() validation: JuiValidationEvents = JuiValidationEvents.onLoad;

  /**
   * Set the size of the radio-group. Options are 'md', 'lg' or 'sm'. Defaults to 'md'
   */
  @Prop() size: JuiControlSize = JuiControlSize.md;

  /**
   * The errors for the input
   */
  @Prop() errors: boolean | { [key: string]: string };

  /**
   * The validation messages for the input
   */
  @Prop() messages: string | { [key: string]: string };

  /**
   * Optional data-qa string
   */
  @Prop() dataqastring: string = null;

  /**
   * Optional help string
   */
  @Prop() help: string = null;

  /**
   * Optional tooltip string
   */
  @Prop() tooltip: string = null;

  /**
   * Internal prop
   */
  @Prop() storybook = false;

  private errorMessages = null;

  @State()
  private touched = false;

  @State()
  private currentSelectedIndex = 0;

  @State()
  private radios: HTMLJuiRadioElement[] = [];

  @State()
  private tooltipLeft = false;

  @State()
  private tooltipLeftBottom = false;

  @Element() el: HTMLElement;

  @Event() public juiChange: EventEmitter<number | string>;

  @Watch('value')
  valueChanged(value: any | undefined): void {
    this.setRadioTabindex(value).then();
  }

  @Listen('juiChecked')
  juiCheckedListener(evt: CustomEvent): void {
    const changed = this.value !== evt.detail;
    this.value = evt.detail;
    this.radios.filter((x: any) => x.value !== this.value).map((x: HTMLJuiRadioElement) => (x.checked = false));
    this.setSelectedIndex();
    if (changed) {
      this.onChange();
    }
  }

  @Listen('keydown')
  keydownHandler(evt: KeyboardEvent): void {
    let nextRadio: HTMLJuiRadioElement;
    if (evt.key === 'ArrowRight' || evt.key === 'ArrowDown') {
      evt.preventDefault();
      nextRadio =
        this.currentSelectedIndex === this.radios.length - 1
          ? this.radios[0]
          : this.radios[this.currentSelectedIndex + 1];
      nextRadio.setAttribute('checked', 'true');
    }
    if (evt.key === 'ArrowLeft' || evt.key === 'ArrowUp') {
      evt.preventDefault();
      nextRadio =
        this.currentSelectedIndex === 0
          ? this.radios[this.radios.length - 1]
          : this.radios[this.currentSelectedIndex - 1];
      nextRadio.setAttribute('checked', 'true');
    }
  }

  componentDidRender(): void {
    if (this.tooltip) {
      this.renderTooltips();
    }
  }

  async componentDidLoad(): Promise<void> {
    await this.setRadioTabindex(this.value);
    if (this.value) {
      this.radios.find((x: any) => x.value === this.value)?.setAttribute('checked', 'true');
    }
    this.initRadioElements();
  }

  componentWillLoad(): void {
    this.radios = this.getRadios();
    this.setSelectedIndex();
    if (this.validation === JuiValidationEvents.onLoad) {
      this.touched = true;
      this.renderError();
    }
  }

  componentWillUpdate(): void {
    this.renderError();
  }

  private onChange(): void {
    this.juiChange.emit(this.value);
  }

  private getRadios(): HTMLJuiRadioElement[] {
    return Array.from(this.el.children) as HTMLJuiRadioElement[];
  }

  private setSelectedIndex(): void {
    const activeIndex = this.radios.findIndex((x: any) => x.label === this.value);
    this.currentSelectedIndex = activeIndex !== -1 ? activeIndex : 0;
  }

  private setRadioTabindex = async (value: any | undefined): Promise<void> => {
    // Get the first radio that is not disabled and the checked one
    const first = this.radios.find(radio => !radio.disable);
    const checked = this.radios.find(radio => radio.value === value && !radio.disable);

    if (!first && !checked) {
      return;
    }

    const focusable = checked || first;
    for (const radio of this.radios) {
      const tabindex = radio === focusable ? 0 : -1;
      if (radio === focusable && value) {
        if (!this.storybook) {
          await radio.setFocused();
        }
      }
      await radio.setButtonTabindex(tabindex);
    }
  };

  private initRadioElements(): void {
    this.radios.forEach((radio: HTMLJuiRadioElement) => {
      if (this.disable) {
        // if radio group is disabled then disable all radio children
        radio['disable'] = true;
      }
      // update radio child sizes from the group
      radio.size = this.size;
    });
  }

  render() {
    const errorFragment = this.errorMessages ? this.errorMessages : null;
    const dataQaTagLabel = this.dataqastring ? `${this.dataqastring}_formFieldLabel` : null;

    const helpText = this.help ? <div class="jui-control__help">{this.help}</div> : null;

    const tooltip = this.tooltip ? (
      <div
        class={{
          'jui-control__tooltip': true,
          [`jui-control__tooltip--input-${this.size}`]: true
        }}
      >
        <jui-icon
          class={{ 'jui-control__tooltip-icon': true }}
          name="help_outline"
          cursor="help"
          color={IconVariant.info}
          size={IconSize.xs}
          tabindex={-1}
        ></jui-icon>
        <span
          class={{
            'jui-control__tooltip-text': true,
            'jui-control__tooltip-text--left': this.tooltipLeft && !this.tooltipLeftBottom,
            'jui-control__tooltip-text--left-bottom': this.tooltipLeftBottom
          }}
        >
          {this.tooltip}
        </span>
      </div>
    ) : null;

    return (
      <Host data-qa={this.dataqastring || null}>
        <div class="jui-control">
          <fieldset
          role="radiogroup"
          class={{
            'jui-control__fieldset': true,
            [`jui-control__fieldset--${this.display}`]: true,
            [`jui-control__fieldset--${this.size}`]: true,
            'jui-control__fieldset--invalid': !!this.errors && this.touched
          }}
        >
          <legend
            {...(dataQaTagLabel && { 'data-qa': dataQaTagLabel })}
            class={{
              'jui-control__legend': true,
              'jui-control__legend--has-value': !!this.value,
              'jui-control__legend--disabled': !!this.disable,
              'jui-control__legend--has-tooltip': !!this.tooltip
            }}
          >
            <div
              class={{
                'jui-control__legend-text': true
              }}
            >
              {this.required ? `${this.label} *` : this.label}
            </div>
            {tooltip}
          </legend>
          {helpText}
          <slot></slot>
        </fieldset>
        {errorFragment}
        </div>
      </Host>
    );
  }

  private renderTooltips(): void {
    this.tooltipLeft = renderTooltipOnLeft(this.el, 'jui-control__fieldset');
    if (this.tooltipLeft) {
      this.tooltipLeftBottom = renderTooltipOnBottom(this.el, 'jui-control__fieldset');
    }
  }

  private renderError(): void {
    this.errorMessages = renderFormError(this.errors, this.messages);
  }
}
